import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { X, Camera, RefreshCcw, AlertTriangle } from 'lucide-react';

const QRScanner = ({ onScan, onClose }) => {
    const scannerRef = useRef(null);
    const containerRef = useRef(null);
    const [scannerStatus, setScannerStatus] = useState('initializing'); // 'initializing', 'active', 'error'
    const [errorMsg, setErrorMsg] = useState(null);

    useEffect(() => {
        // Generar un ID único para evitar colisiones si el componente se monta rápido varias veces
        const qrCodeId = "qr-reader-container";

        let html5QrCode;

        const startScanner = async () => {
            try {
                html5QrCode = new Html5Qrcode(qrCodeId);
                scannerRef.current = html5QrCode;

                const config = {
                    fps: 10,
                    qrbox: { width: 250, height: 250 },
                    aspectRatio: 1.0,
                    formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE]
                };

                await html5QrCode.start(
                    { facingMode: "environment" },
                    config,
                    (decodedText) => {
                        // Éxito: Detener antes de devolver el control al padre
                        handleStop().then(() => {
                            onScan(decodedText);
                        });
                    },
                    () => { /* Ignorar errores de escaneo fallido momentáneo */ }
                );

                setScannerStatus('active');
            } catch (err) {
                console.error("Error starting QR scanner:", err);
                setErrorMsg("No se pudo iniciar la cámara. Verifica los permisos.");
                setScannerStatus('error');
            }
        };

        const handleStop = async () => {
            if (html5QrCode && html5QrCode.isScanning) {
                try {
                    await html5QrCode.stop();
                } catch (err) {
                    console.error("Error stopping scanner:", err);
                }
            }
        };

        startScanner();

        return () => {
            // Limpieza al desmontar
            handleStop();
        };
    }, []);

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-85 p-4 backdrop-blur-md">
            <div className="bg-white w-full max-w-md rounded-3xl overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
                <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-blue-600 rounded-xl text-white shadow-lg shadow-blue-200">
                            <Camera className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 leading-none">Escanear Bodega</h3>
                            <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mt-1">Cámara Activa</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-400 hover:text-gray-600"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6">
                    <div className="relative rounded-2xl overflow-hidden bg-black aspect-square shadow-inner border-4 border-gray-100">
                        {/* 
                            IMPORTANTE: El div "qr-reader-container" DEBE estar vacío.
                            React no debe poner ningún componente hijo aquí porque html5-qrcode 
                            inyectará un <video> y React se perderá si intenta conciliarlo.
                        */}
                        <div id="qr-reader-container" className="w-full h-full" ref={containerRef}></div>

                        {/* UI Indicators (Hermanos del contenedor, no hijos) */}
                        {scannerStatus === 'initializing' && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white bg-black">
                                <RefreshCcw className="w-10 h-10 animate-spin text-blue-400" />
                                <p className="text-sm font-bold tracking-wide">Accediendo a cámara...</p>
                            </div>
                        )}

                        {scannerStatus === 'error' && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-red-400 p-8 text-center bg-gray-900">
                                <AlertTriangle className="w-12 h-12" />
                                <p className="text-sm font-black leading-tight">{errorMsg}</p>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-full text-xs font-bold hover:bg-red-500/20 transition-all text-red-500"
                                >
                                    Reintentar Página
                                </button>
                            </div>
                        )}

                        {/* Scanner Decoration Overlay */}
                        {scannerStatus === 'active' && (
                            <div className="absolute inset-0 pointer-events-none flex items-center justify-center p-12">
                                <div className="w-full h-full border-2 border-blue-400 border-dashed rounded-3xl opacity-60 relative">
                                    <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-blue-500 rounded-tl-lg"></div>
                                    <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-blue-500 rounded-tr-lg"></div>
                                    <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-blue-500 rounded-bl-lg"></div>
                                    <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-blue-500 rounded-br-lg"></div>

                                    {/* Scanning Line Animation */}
                                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent animate-scan-line"></div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mt-8 space-y-4">
                        <div className="flex items-start gap-3.5 p-4 bg-blue-50/50 rounded-2xl border border-blue-100">
                            <div className="p-2 bg-white rounded-xl text-blue-600 shadow-sm">
                                <Camera className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-xs font-black text-blue-900 uppercase tracking-widest mb-1">Verificación Directa</h4>
                                <p className="text-sm text-blue-800 leading-snug">
                                    Apunta la cámara al código QR ubicado en la entrada de la bodega.
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={onClose}
                            className="w-full py-4 px-6 bg-gray-50 hover:bg-gray-100 text-gray-500 font-bold rounded-2xl transition-all border border-gray-100 tracking-wide"
                        >
                            CANCELAR
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                #qr-reader-container video {
                    width: 100% !important;
                    height: 100% !important;
                    object-fit: cover !important;
                }
                @keyframes scan-line {
                    0% { top: 0%; opacity: 0; }
                    20% { opacity: 1; }
                    80% { opacity: 1; }
                    100% { top: 100%; opacity: 0; }
                }
                .animate-scan-line {
                    animation: scan-line 2.5s infinite linear;
                }
            `}</style>
        </div>
    );
};

export default QRScanner;
